import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  MessageCircle,
  Copy,
  Lightbulb,
  Wrench,
  CircleDollarSign,
  FileText,
  ClipboardList,
  Target,
  Award,
  AlertCircle,
  Sparkles,
  HelpCircle,
  ArrowRight,
  Check,
  Clock,
  Package,
  ShieldAlert,
  BadgeCheck,
  ThumbsUp,
  Gauge,
  Calculator,
  ListTodo,
  BookOpen,
  Hammer,
  Info
} from 'lucide-react';
import { Button } from './ui/button';
import LeonAvatar from './LeonAvatar';
import { toast } from 'sonner';

// ============================================
// COMPOSANT ACCORDÉON PREMIUM
// ============================================
const AccordionSection = ({ 
  id,
  title, 
  icon: Icon, 
  children, 
  defaultOpen = false,
  priority = false,
  badge = null,
  badgeColor = 'amber'
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div 
      data-testid={`accordion-${id}`}
      className={`
        border rounded-xl overflow-hidden transition-all duration-300
        ${priority ? 'border-amber-500/50 bg-gradient-to-r from-amber-500/5 to-transparent' : 'border-zinc-800 bg-zinc-900/50'}
        ${isOpen ? 'shadow-lg' : 'hover:border-zinc-700'}
      `}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-800/30 transition-colors"
        data-testid={`accordion-toggle-${id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${priority ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-400'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <span className="font-medium text-white">{title}</span>
          {badge && (
            <span className={`
              px-2 py-0.5 text-xs font-medium rounded-full
              ${badgeColor === 'red' ? 'bg-red-500/20 text-red-400' : ''}
              ${badgeColor === 'amber' ? 'bg-amber-500/20 text-amber-400' : ''}
              ${badgeColor === 'green' ? 'bg-emerald-500/20 text-emerald-400' : ''}
              ${badgeColor === 'blue' ? 'bg-blue-500/20 text-blue-400' : ''}
            `}>
              {badge}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 border-t border-zinc-800/50">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// CARTE DE VERDICT / SCORE
// ============================================
const VerdictCard = ({ verdict, score, mainObservation }) => {
  const getVerdictStyle = () => {
    switch(verdict?.toLowerCase()) {
      case 'favorable':
        return { bg: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/30', icon: CheckCircle, color: 'text-emerald-400' };
      case 'à négocier':
        return { bg: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/30', icon: AlertCircle, color: 'text-amber-400' };
      case 'attention requise':
        return { bg: 'from-orange-500/20 to-orange-500/5', border: 'border-orange-500/30', icon: AlertTriangle, color: 'text-orange-400' };
      case 'défavorable':
        return { bg: 'from-red-500/20 to-red-500/5', border: 'border-red-500/30', icon: XCircle, color: 'text-red-400' };
      default:
        return { bg: 'from-zinc-500/20 to-zinc-500/5', border: 'border-zinc-500/30', icon: HelpCircle, color: 'text-zinc-400' };
    }
  };

  const style = getVerdictStyle();
  const Icon = style.icon;

  return (
    <div className={`p-6 rounded-2xl bg-gradient-to-br ${style.bg} border ${style.border}`} data-testid="verdict-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon className={`w-8 h-8 ${style.color}`} />
          <div>
            <div className="text-sm text-zinc-400">Verdict de Léon</div>
            <div className={`text-xl font-bold ${style.color}`}>{verdict || 'En cours d\'analyse'}</div>
          </div>
        </div>
        {score !== undefined && (
          <div className="text-right">
            <div className="text-sm text-zinc-400">Score de fiabilité</div>
            <div className={`text-3xl font-bold ${
              score >= 70 ? 'text-emerald-400' : 
              score >= 50 ? 'text-amber-400' : 'text-red-400'
            }`}>{score}/100</div>
          </div>
        )}
      </div>
      {mainObservation && (
        <p className="text-zinc-300 text-sm leading-relaxed">{mainObservation}</p>
      )}
    </div>
  );
};

// ============================================
// SECTION RISQUES
// ============================================
const RisksSection = ({ risks }) => {
  if (!risks || risks.length === 0) {
    return (
      <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <span className="text-emerald-300">Aucun risque majeur détecté dans ce devis.</span>
      </div>
    );
  }

  const getSeverityStyle = (severity) => {
    switch(severity) {
      case 'high': return { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'text-red-400', badge: 'bg-red-500/20 text-red-400' };
      case 'medium': return { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400' };
      default: return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-400' };
    }
  };

  const severityLabels = { high: 'Élevé', medium: 'Moyen', low: 'Faible' };

  return (
    <div className="space-y-3">
      {risks.map((risk, idx) => {
        const style = getSeverityStyle(risk.severity);
        return (
          <div 
            key={idx} 
            className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
            data-testid={`risk-item-${idx}`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 ${style.icon} flex-shrink-0 mt-0.5`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-white">{risk.risk_title}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${style.badge}`}>
                    {severityLabels[risk.severity] || risk.severity}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 mb-2">{risk.risk_description}</p>
                {risk.consequence && (
                  <p className="text-sm text-zinc-400 italic">
                    <span className="text-amber-400">Conséquence:</span> {risk.consequence}
                  </p>
                )}
                {risk.recommendation && (
                  <div className="mt-2 p-2 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-emerald-400 flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{risk.recommendation}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// SECTION QUESTIONS ARTISAN (avec bouton copier)
// ============================================
const QuestionsSection = ({ questions }) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const copyAllQuestions = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!questions || questions.length === 0) return;
    
    const text = questions.map((q, i) => `${i + 1}. ${q.question}`).join('\n\n');
    
    // Essayer plusieurs méthodes de copie
    let success = false;
    
    // Méthode 1: Clipboard API moderne
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch (err) {
        console.log('Clipboard API failed, trying fallback...');
      }
    }
    
    // Méthode 2: execCommand fallback
    if (!success) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, text.length);
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (err) {
        console.log('execCommand fallback failed');
      }
    }
    
    if (success) {
      setCopied(true);
      setCopyError(false);
      toast.success('Questions copiées !');
      setTimeout(() => setCopied(false), 2500);
    } else {
      setCopyError(true);
      // Afficher le texte dans une alerte pour que l'utilisateur puisse le copier
      toast.error(
        <div>
          <p className="font-medium mb-2">Copie automatique impossible.</p>
          <p className="text-xs">Cliquez sur une question pour la sélectionner.</p>
        </div>
      );
      setTimeout(() => setCopyError(false), 3000);
    }
  }, [questions]);

  if (!questions || questions.length === 0) {
    return (
      <div className="text-zinc-400 text-sm">
        Aucune question spécifique à poser pour ce devis.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={copyAllQuestions}
          type="button"
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
            transition-all duration-200 cursor-pointer
            ${copied 
              ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400' 
              : copyError
                ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                : 'bg-amber-500/10 border border-amber-500/50 text-amber-400 hover:bg-amber-500/20 hover:border-amber-500'
            }
          `}
          data-testid="copy-questions-btn"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copié !' : 'Copier toutes les questions'}
        </button>
      </div>
      
      <div className="space-y-3">
        {questions.map((q, idx) => (
          <div 
            key={idx} 
            className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 cursor-pointer hover:border-amber-500/30 transition-colors"
            data-testid={`question-item-${idx}`}
            onClick={() => {
              const questionText = q.question;
              navigator.clipboard?.writeText(questionText).then(() => {
                toast.success(`Question ${idx + 1} copiée !`);
              }).catch(() => {});
            }}
            title="Cliquez pour copier cette question"
          >
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-amber-400">{idx + 1}</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-white mb-1">{q.question}</p>
                {q.why_important && (
                  <p className="text-sm text-zinc-400">
                    <span className="text-amber-400">Pourquoi c'est important:</span> {q.why_important}
                  </p>
                )}
                {q.expected_answer && (
                  <p className="text-sm text-zinc-500 mt-1">
                    <span className="text-emerald-400">Bonne réponse attendue:</span> {q.expected_answer}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// SECTION TRADUCTION PRESTATIONS
// ============================================
const PrestationsSection = ({ prestations }) => {
  if (!prestations || prestations.length === 0) {
    return <div className="text-zinc-400 text-sm">Aucune prestation à traduire.</div>;
  }

  // Formater le prix pour l'affichage
  const formatPrice = (price) => {
    if (price === null || price === undefined) return null;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return null;
    return numPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-4">
      {/* Compteur de prestations */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">
          {prestations.length} prestation{prestations.length > 1 ? 's' : ''} détectée{prestations.length > 1 ? 's' : ''}
        </span>
      </div>

      {prestations.map((p, idx) => {
        const priceHt = formatPrice(p.price_ht);
        const priceTtc = formatPrice(p.price_ttc);
        const isZeroPrice = (p.price_ht === 0 || p.price_ht === "0" || p.price_ht === "0.00") && 
                           (p.price_ttc === 0 || p.price_ttc === "0" || p.price_ttc === "0.00" || !p.price_ttc);
        const hasAlert = p.alert && p.alert !== "null" && p.alert !== null;

        return (
          <div 
            key={idx} 
            className={`p-4 rounded-lg border ${
              hasAlert || isZeroPrice
                ? 'bg-amber-500/5 border-amber-500/30' 
                : 'bg-zinc-800/30 border-zinc-700/30'
            }`}
            data-testid={`prestation-item-${idx}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-1">
                {/* Référence si présente */}
                {p.reference && p.reference !== "X" && (
                  <div className="text-xs text-amber-400 mb-1 font-mono">Réf: {p.reference}</div>
                )}

                <div className="text-xs text-zinc-500 mb-1">Description technique</div>
                <p className="text-sm text-zinc-400 mb-3 font-mono">{p.original}</p>
                
                <div className="flex items-center gap-2 mb-1">
                  <ArrowRight className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-amber-400">En langage simple</span>
                </div>
                <p className="text-white">{p.simplified}</p>

                {/* Quantité et unité si présentes */}
                {(p.quantity || p.unit) && (
                  <div className="mt-2 text-xs text-zinc-500">
                    {p.quantity && <span>Quantité: {p.quantity}</span>}
                    {p.quantity && p.unit && <span> • </span>}
                    {p.unit && <span>Unité: {p.unit}</span>}
                  </div>
                )}
                
                {/* Alerte spécifique */}
                {hasAlert && (
                  <div className="mt-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{p.alert}</p>
                    </div>
                  </div>
                )}

                {/* Commentaire de Léon */}
                {p.leon_comment && (
                  <div className="mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-200">{p.leon_comment}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Prix - Afficher même si 0€ */}
              <div className="text-right flex-shrink-0 min-w-[80px]">
                {isZeroPrice ? (
                  <div className="text-lg font-bold text-amber-400">0</div>
                ) : (
                  <>
                    {priceHt && <div className="text-sm text-zinc-400">{priceHt}€ HT</div>}
                    {priceTtc && <div className="text-lg font-bold text-white">{priceTtc}€ TTC</div>}
                    {!priceHt && !priceTtc && <div className="text-sm text-zinc-500">-</div>}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// SECTION ASSURANCE
// ============================================
const InsuranceSection = ({ insurance }) => {
  const isMissing = !insurance?.insurance_mentioned;

  return (
    <div className="space-y-4">
      {/* Alerte principale */}
      <div className={`p-4 rounded-lg border ${
        isMissing 
          ? 'bg-red-500/10 border-red-500/30' 
          : 'bg-emerald-500/10 border-emerald-500/30'
      }`}>
        <div className="flex items-start gap-3">
          {isMissing ? (
            <ShieldAlert className="w-6 h-6 text-red-400 flex-shrink-0" />
          ) : (
            <BadgeCheck className="w-6 h-6 text-emerald-400 flex-shrink-0" />
          )}
          <div>
            <div className={`font-medium ${isMissing ? 'text-red-400' : 'text-emerald-400'}`}>
              {isMissing ? 'Assurance non mentionnée dans le devis' : 'Assurance mentionnée'}
            </div>
            {insurance?.insurance_details && (
              <p className="text-sm text-zinc-300 mt-1">{insurance.insurance_details}</p>
            )}
          </div>
        </div>
      </div>

      {/* Message pédagogique de Léon */}
      <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <div className="font-medium text-white mb-2">Ce que Léon te conseille</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              {insurance?.leon_warning || 
                "Avant de signer un devis travaux, demande toujours l'attestation d'assurance professionnelle de l'entreprise. Cette assurance est obligatoire pour les entreprises du bâtiment et te protège en cas de problème."}
            </p>
          </div>
        </div>
      </div>

      {/* Activités à vérifier */}
      {insurance?.activities_to_verify && insurance.activities_to_verify.length > 0 && (
        <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
          <div className="font-medium text-amber-400 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Activités à vérifier dans l'attestation
          </div>
          <ul className="space-y-1">
            {insurance.activities_to_verify.map((activity, idx) => (
              <li key={idx} className="text-sm text-zinc-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                {activity}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action requise */}
      <div className="p-4 bg-gradient-to-r from-amber-500/20 to-transparent rounded-lg border border-amber-500/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="font-medium text-amber-400">Action recommandée</div>
            <p className="text-sm text-zinc-300">
              {insurance?.action_required || "Demander l'attestation d'assurance décennale avant de signer"}
            </p>
          </div>
        </div>
      </div>

      {/* Rappel rassurant */}
      <div className="p-3 bg-zinc-800/30 rounded-lg text-center">
        <p className="text-xs text-zinc-400">
          <span className="text-emerald-400">Bon à savoir :</span> Demander l'attestation d'assurance n'est pas gênant. 
          C'est une démarche normale et responsable que tout particulier devrait faire.
        </p>
      </div>
    </div>
  );
};

// ============================================
// SECTION BUDGET
// ============================================
const BudgetSection = ({ budget, priceAnalysis }) => {
  const quoteTTC = budget?.quote_total_ttc || priceAnalysis?.total_ttc || 0;
  const estimatedLow = budget?.estimated_final_budget_low || quoteTTC;
  const estimatedHigh = budget?.estimated_final_budget_high || quoteTTC * 1.15;

  return (
    <div className="space-y-4">
      {/* Comparaison visuelle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 text-center">
          <div className="text-sm text-zinc-400 mb-1">Total du devis</div>
          <div className="text-2xl font-bold text-white">{quoteTTC?.toLocaleString()}€ TTC</div>
        </div>
        <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
          <div className="text-sm text-amber-400 mb-1">Budget probable final</div>
          <div className="text-2xl font-bold text-amber-400">
            {estimatedLow?.toLocaleString()}€ - {estimatedHigh?.toLocaleString()}€
          </div>
        </div>
      </div>

      {/* Détail des coûts supplémentaires */}
      {budget?.additional_costs_breakdown && budget.additional_costs_breakdown.length > 0 && (
        <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
          <div className="font-medium text-white mb-3 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-amber-400" />
            Coûts supplémentaires possibles
          </div>
          <div className="space-y-2">
            {budget.additional_costs_breakdown.map((cost, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex-1">
                  <span className="text-zinc-300">{cost.item}</span>
                  {cost.reason && (
                    <span className="text-zinc-500 ml-2">({cost.reason})</span>
                  )}
                </div>
                <span className="text-amber-400 font-medium">+{cost.estimated_cost?.toLocaleString() || '?'}€</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conseil de Léon */}
      {budget?.leon_budget_advice && (
        <div className="p-4 bg-gradient-to-r from-amber-500/10 to-transparent rounded-lg border border-amber-500/20">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300">{budget.leon_budget_advice}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// SECTION ASTUCES DE LÉON
// ============================================
const TipsSection = ({ tips }) => {
  if (!tips || tips.length === 0) return null;

  const categoryIcons = {
    negotiation: CircleDollarSign,
    protection: Shield,
    verification: CheckCircle,
    practical: Wrench
  };

  const categoryColors = {
    negotiation: 'text-emerald-400 bg-emerald-500/20',
    protection: 'text-blue-400 bg-blue-500/20',
    verification: 'text-amber-400 bg-amber-500/20',
    practical: 'text-purple-400 bg-purple-500/20'
  };

  return (
    <div className="space-y-3">
      {tips.map((tip, idx) => {
        const Icon = categoryIcons[tip.tip_category] || Lightbulb;
        const colorClass = categoryColors[tip.tip_category] || 'text-amber-400 bg-amber-500/20';
        
        return (
          <div 
            key={idx} 
            className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/30"
            data-testid={`tip-item-${idx}`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                <Icon className={`w-4 h-4 ${colorClass.split(' ')[0]}`} />
              </div>
              <div>
                <div className="font-medium text-white mb-1">{tip.tip_title}</div>
                <p className="text-sm text-zinc-300">{tip.tip_content}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// SECTION "SI C'ÉTAIT MON CHANTIER"
// ============================================
const MyProjectSection = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-4">
      {data.intro && (
        <div className="p-4 bg-gradient-to-r from-amber-500/20 to-transparent rounded-lg border border-amber-500/30">
          <div className="flex items-start gap-3">
            <LeonAvatar size="sm" />
            <p className="text-amber-200 font-medium">{data.intro}</p>
          </div>
        </div>
      )}

      {data.checkpoints && data.checkpoints.length > 0 && (
        <div className="space-y-3">
          {data.checkpoints.map((cp, idx) => (
            <div 
              key={idx} 
              className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50"
              data-testid={`checkpoint-item-${idx}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-amber-400">{idx + 1}</span>
                </div>
                <div>
                  <div className="font-medium text-white">{cp.checkpoint}</div>
                  {cp.why && <p className="text-sm text-zinc-400 mt-1">{cp.why}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.final_advice && (
        <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
          <div className="flex items-start gap-3">
            <ThumbsUp className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-emerald-200">{data.final_advice}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// SECTION POSTES MANQUANTS
// ============================================
const MissingItemsSection = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <span className="text-emerald-300">Le devis semble complet, aucun poste majeur potentiellement oublié.</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div 
          key={idx} 
          className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30"
          data-testid={`missing-item-${idx}`}
        >
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-400">{item.item}</div>
              {item.why_usually_needed && (
                <p className="text-sm text-zinc-300 mt-1">{item.why_usually_needed}</p>
              )}
              {item.estimated_cost && (
                <p className="text-sm text-zinc-400 mt-1">
                  <span className="text-amber-400">Coût estimé:</span> {item.estimated_cost}
                </p>
              )}
              {item.question_to_ask && (
                <div className="mt-2 p-2 bg-zinc-800/50 rounded-lg">
                  <p className="text-sm text-zinc-300 flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span><strong>À demander:</strong> {item.question_to_ask}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// SECTION ÉTAPES DU CHANTIER
// ============================================
const PhasesSection = ({ phases }) => {
  if (!phases || phases.length === 0) return null;

  return (
    <div className="relative">
      {/* Ligne verticale de connexion */}
      <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-zinc-700" />
      
      <div className="space-y-4">
        {phases.map((phase, idx) => (
          <div key={idx} className="flex items-start gap-4" data-testid={`phase-item-${idx}`}>
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 z-10 border-2 border-zinc-900">
              <span className="font-bold text-amber-400">{phase.phase_number || idx + 1}</span>
            </div>
            <div className="flex-1 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
              <div className="font-medium text-white">{phase.phase_title}</div>
              <p className="text-sm text-zinc-400 mt-1">{phase.phase_description}</p>
              {phase.estimated_duration && (
                <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                  <Clock className="w-3 h-3" />
                  <span>{phase.estimated_duration}</span>
                </div>
              )}
              {phase.key_points && phase.key_points.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {phase.key_points.map((point, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-zinc-800 rounded-full text-zinc-400">
                      {point}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// SECTION SCORE DE FIABILITÉ
// ============================================
const ReliabilitySection = ({ reliability, globalScore }) => {
  const score = reliability?.global_score || globalScore || 0;
  const adminScore = reliability?.administrative_score || { score: 0, max: 30 };
  const techScore = reliability?.technical_score || { score: 0, max: 40 };
  const finScore = reliability?.financial_score || { score: 0, max: 30 };

  const getScoreColor = (score, max) => {
    const percent = (score / max) * 100;
    if (percent >= 70) return 'bg-emerald-500';
    if (percent >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const ScoreBar = ({ label, score, max, details }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-400">{label}</span>
        <span className="font-medium text-white">{score}/{max}</span>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getScoreColor(score, max)} transition-all duration-500`}
          style={{ width: `${(score / max) * 100}%` }}
        />
      </div>
      {details && <p className="text-xs text-zinc-500">{details}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Score global */}
      <div className="text-center p-6 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
        <div className="text-sm text-zinc-400 mb-2">Score de fiabilité global</div>
        <div className={`text-5xl font-bold ${
          score >= 70 ? 'text-emerald-400' : 
          score >= 50 ? 'text-amber-400' : 'text-red-400'
        }`}>
          {score}<span className="text-2xl text-zinc-500">/100</span>
        </div>
        <div className="mt-4 flex justify-center">
          <Gauge className={`w-12 h-12 ${
            score >= 70 ? 'text-emerald-400' : 
            score >= 50 ? 'text-amber-400' : 'text-red-400'
          }`} />
        </div>
      </div>

      {/* Détail par catégorie */}
      <div className="space-y-4">
        <ScoreBar 
          label="Administratif" 
          score={adminScore.score || Math.round(score * 0.3)} 
          max={adminScore.max || 30}
          details={adminScore.details}
        />
        <ScoreBar 
          label="Technique" 
          score={techScore.score || Math.round(score * 0.4)} 
          max={techScore.max || 40}
          details={techScore.details}
        />
        <ScoreBar 
          label="Financier" 
          score={finScore.score || Math.round(score * 0.3)} 
          max={finScore.max || 30}
          details={finScore.details}
        />
      </div>
    </div>
  );
};

// ============================================
// SECTION POINTS POSITIFS
// ============================================
const PositivePointsSection = ({ points }) => {
  if (!points || points.length === 0) return null;

  return (
    <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
      <div className="font-medium text-emerald-400 mb-3 flex items-center gap-2">
        <ThumbsUp className="w-5 h-5" />
        Points positifs du devis
      </div>
      <ul className="space-y-2">
        {points.map((point, idx) => (
          <li key={idx} className="text-sm text-emerald-200 flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
export default function EnrichedQuoteReport({ analysis }) {
  if (!analysis) return null;

  const {
    executive_summary,
    prestations_translated,
    detected_risks,
    questions_for_artisan,
    potentially_missing_items,
    insurance_check,
    reliability_score,
    budget_estimation,
    leon_tips,
    if_it_was_my_project,
    project_phases,
    positive_points,
    global_score,
    price_analysis,
    report
  } = analysis;

  // Calculer les badges pour les sections
  const risksCount = detected_risks?.length || 0;
  const questionsCount = questions_for_artisan?.length || 0;
  const missingCount = potentially_missing_items?.length || 0;

  return (
    <div className="space-y-6" data-testid="enriched-quote-report">
      {/* ===== RÉSUMÉ EXÉCUTIF ===== */}
      <VerdictCard 
        verdict={executive_summary?.verdict}
        score={reliability_score?.global_score || global_score}
        mainObservation={executive_summary?.main_observation}
      />

      {/* Points clés */}
      {executive_summary?.key_points && executive_summary.key_points.length > 0 && (
        <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
          <div className="font-medium text-white mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />
            Points clés à retenir
          </div>
          <ul className="space-y-2">
            {executive_summary.key_points.map((point, idx) => (
              <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action recommandée */}
      {executive_summary?.recommended_action && (
        <div className="p-4 bg-gradient-to-r from-amber-500/20 to-transparent rounded-lg border border-amber-500/30">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-xs text-amber-400">Action recommandée par Léon</div>
              <div className="text-white font-medium">{executive_summary.recommended_action}</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ACCORDÉONS ===== */}
      <div className="space-y-3">
        {/* Risques (priorité haute) */}
        <AccordionSection
          id="risks"
          title="Risques détectés"
          icon={AlertTriangle}
          defaultOpen={risksCount > 0}
          priority={risksCount > 0}
          badge={risksCount > 0 ? `${risksCount} risque${risksCount > 1 ? 's' : ''}` : null}
          badgeColor="red"
        >
          <RisksSection risks={detected_risks} />
        </AccordionSection>

        {/* Questions à poser (priorité haute) */}
        <AccordionSection
          id="questions"
          title="Questions à poser à l'artisan"
          icon={MessageCircle}
          defaultOpen={questionsCount > 0}
          priority={questionsCount > 0}
          badge={questionsCount > 0 ? `${questionsCount} question${questionsCount > 1 ? 's' : ''}` : null}
          badgeColor="amber"
        >
          <QuestionsSection questions={questions_for_artisan} />
        </AccordionSection>

        {/* Assurance (toujours prioritaire) */}
        <AccordionSection
          id="insurance"
          title="Vérification de l'assurance"
          icon={Shield}
          defaultOpen={true}
          priority={!insurance_check?.insurance_mentioned}
          badge={!insurance_check?.insurance_mentioned ? 'À vérifier' : 'OK'}
          badgeColor={!insurance_check?.insurance_mentioned ? 'red' : 'green'}
        >
          <InsuranceSection insurance={insurance_check} />
        </AccordionSection>

        {/* Astuces de Léon (priorité haute) */}
        <AccordionSection
          id="tips"
          title="Astuces de Léon"
          icon={Lightbulb}
          defaultOpen={true}
          priority={true}
        >
          <TipsSection tips={leon_tips} />
        </AccordionSection>

        {/* Traduction des prestations */}
        <AccordionSection
          id="prestations"
          title="Comprendre les prestations"
          icon={BookOpen}
          defaultOpen={false}
        >
          <PrestationsSection prestations={prestations_translated} />
        </AccordionSection>

        {/* Postes potentiellement manquants */}
        <AccordionSection
          id="missing"
          title="Postes potentiellement oubliés"
          icon={Package}
          defaultOpen={missingCount > 0}
          badge={missingCount > 0 ? `${missingCount} poste${missingCount > 1 ? 's' : ''}` : null}
          badgeColor="amber"
        >
          <MissingItemsSection items={potentially_missing_items} />
        </AccordionSection>

        {/* Budget réel */}
        <AccordionSection
          id="budget"
          title="Estimation du budget réel"
          icon={CircleDollarSign}
          defaultOpen={false}
        >
          <BudgetSection budget={budget_estimation} priceAnalysis={price_analysis} />
        </AccordionSection>

        {/* Score de fiabilité */}
        <AccordionSection
          id="reliability"
          title="Score de fiabilité du devis"
          icon={Gauge}
          defaultOpen={false}
        >
          <ReliabilitySection reliability={reliability_score} globalScore={global_score} />
        </AccordionSection>

        {/* Si c'était mon chantier */}
        <AccordionSection
          id="my-project"
          title="Si c'était mon chantier"
          icon={Hammer}
          defaultOpen={false}
        >
          <MyProjectSection data={if_it_was_my_project} />
        </AccordionSection>

        {/* Étapes du chantier */}
        {project_phases && project_phases.length > 0 && (
          <AccordionSection
            id="phases"
            title="Étapes probables du chantier"
            icon={ListTodo}
            defaultOpen={false}
          >
            <PhasesSection phases={project_phases} />
          </AccordionSection>
        )}

        {/* Points positifs */}
        {positive_points && positive_points.length > 0 && (
          <AccordionSection
            id="positive"
            title="Points positifs"
            icon={ThumbsUp}
            defaultOpen={false}
          >
            <PositivePointsSection points={positive_points} />
          </AccordionSection>
        )}
      </div>
    </div>
  );
}
